import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageTransition, FadeIn, SlideIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronRight, Building2, Users, Calendar, Check, Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { toAbsoluteUrl } from '../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  { value: 'evjf', label: 'EVJF (Enterrement de vie de jeune fille)' },
  { value: 'anniversaire', label: 'Anniversaire' },
  { value: 'teambuilding', label: 'Team Building' },
  { value: 'corporate', label: 'Événement corporate' },
  { value: 'autre', label: 'Autre' }
];

export default function AtelierEvenementielPage() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    eventType: '',
    participantCount: '',
    preferredDate: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // B2B Documents state
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/b2b/documents`);
      setDocuments(response.data || []);
    } catch (err) {
      console.error('Error fetching B2B documents:', err);
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.contactName.trim()) newErrors.contactName = 'Nom requis';
    if (!formData.email.trim()) newErrors.email = 'Email requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email invalide';
    if (!formData.phone.trim()) newErrors.phone = 'Téléphone requis';
    if (!formData.eventType) newErrors.eventType = 'Type d\'événement requis';
    if (!formData.participantCount) newErrors.participantCount = 'Nombre de participants requis';
    if (!formData.message.trim()) newErrors.message = 'Message requis';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const payload = {
        form_type: 'b2b',
        name: formData.contactName,
        company_name: formData.companyName || null,
        email: formData.email,
        phone: formData.phone || null,
        event_type: formData.eventType,
        participant_count: formData.participantCount ? parseInt(formData.participantCount, 10) : null,
        preferred_date: formData.preferredDate || null,
        message: formData.message
      };

      const response = await axios.post(`${API_URL}/contact`, payload);
      if (response.status === 200) {
        toast.success('Votre demande de devis a été envoyée !');
      } else {
        toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
        return;
      }

      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        eventType: '',
        participantCount: '',
        preferredDate: '',
        message: ''
      });
    } catch (err) {
      console.error('Error submitting B2B form:', err);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageTransition>
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden" data-testid="atelier-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1728488447889-13f95adcf5a0?crop=entropy&cs=srgb&fm=jpg&q=85)',
            filter: 'brightness(0.5)'
          }}
        />
        <div className="relative z-10 text-center text-white px-6">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--rose-gold)] mb-4">
            B2B
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light mb-4">
            Atelier Événementiel
          </h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto">
            EVJF · Team Building · Événements privés
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)] transition-colors">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Atelier Événementiel</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-24 px-6" data-testid="atelier-content">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Info Section */}
            <SlideIn direction="left">
              <div>
                <FadeIn>
                  <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-4">
                    Services B2B
                  </p>
                  <h2 className="font-['Cormorant_Garamond'] text-4xl font-light mb-6 text-[var(--charcoal)]">
                    Organisez un moment unique
                  </h2>
                  <p className="text-[var(--charcoal)]/70 mb-8 leading-relaxed">
                    Offrez à vos collaboratrices, clientes ou amies un moment de détente et de beauté inoubliable.
                    Je me déplace dans vos locaux ou vous accueille à l'institut pour des ateliers sur mesure.
                  </p>
                </FadeIn>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4 p-4 bg-white border border-[var(--champagne)]">
                    <div className="w-10 h-10 rounded-full bg-[var(--gold)]/10 flex items-center justify-center shrink-0">
                      <Building2 size={20} className="text-[var(--gold)]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-[var(--charcoal)] mb-1">Team Building</h4>
                      <p className="text-sm text-[var(--charcoal)]/60">
                        Renforcez la cohésion de vos équipes avec un atelier beauté original.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-white border border-[var(--champagne)]">
                    <div className="w-10 h-10 rounded-full bg-[var(--gold)]/10 flex items-center justify-center shrink-0">
                      <Users size={20} className="text-[var(--gold)]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-[var(--charcoal)] mb-1">EVJF & Anniversaires</h4>
                      <p className="text-sm text-[var(--charcoal)]/60">
                        Célébrez ensemble autour d'un moment beauté et bien-être.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-white border border-[var(--champagne)]">
                    <div className="w-10 h-10 rounded-full bg-[var(--gold)]/10 flex items-center justify-center shrink-0">
                      <Calendar size={20} className="text-[var(--gold)]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-[var(--charcoal)] mb-1">Événements Corporate</h4>
                      <p className="text-sm text-[var(--charcoal)]/60">
                        Animations lors de vos événements d'entreprise.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-[var(--champagne)]/30 border border-[var(--champagne)]">
                  <h4 className="font-['Cormorant_Garamond'] text-xl mb-3 text-[var(--charcoal)]">
                    Ce qui est inclus
                  </h4>
                  <ul className="space-y-2">
                    {[
                      'Déplacement dans vos locaux (Paris et proche banlieue)',
                      'Matériel professionnel fourni',
                      'Prestations personnalisées selon vos besoins',
                      'Devis sur mesure gratuit'
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-[var(--charcoal)]/70">
                        <Check size={14} className="text-[var(--gold)]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </SlideIn>

            {/* Form Section */}
            <SlideIn direction="right">
              <form
                onSubmit={handleSubmit}
                className="bg-white p-8 border border-[var(--champagne)]"
                data-testid="devis-form"
              >
                <h3 className="font-['Cormorant_Garamond'] text-2xl mb-6 text-[var(--charcoal)]">
                  Demande de devis
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm text-[var(--charcoal)]/70 mb-2 block">
                      Entreprise (optionnel)
                    </Label>
                    <Input
                      value={formData.companyName}
                      onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                      placeholder="Nom de l'entreprise"
                      className="rounded-none"
                      data-testid="input-company"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[var(--charcoal)]/70 mb-2 block">
                      Nom du contact <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.contactName}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                      placeholder="Votre nom"
                      className={`rounded-none ${errors.contactName ? 'border-red-500' : ''}`}
                      data-testid="input-contact"
                    />
                    {errors.contactName && <p className="text-red-500 text-xs mt-1">{errors.contactName}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm text-[var(--charcoal)]/70 mb-2 block">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@exemple.com"
                      className={`rounded-none ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="input-email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label className="text-sm text-[var(--charcoal)]/70 mb-2 block">
                      Téléphone <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="06 00 00 00 00"
                      className={`rounded-none ${errors.phone ? 'border-red-500' : ''}`}
                      data-testid="input-phone"
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm text-[var(--charcoal)]/70 mb-2 block">
                      Type d'événement <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.eventType}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, eventType: value }))}
                    >
                      <SelectTrigger
                        className={`rounded-none ${errors.eventType ? 'border-red-500' : ''}`}
                        data-testid="select-event-type"
                      >
                        <SelectValue placeholder="Choisir" />
                      </SelectTrigger>
                      <SelectContent>
                        {eventTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.eventType && <p className="text-red-500 text-xs mt-1">{errors.eventType}</p>}
                  </div>
                  <div>
                    <Label className="text-sm text-[var(--charcoal)]/70 mb-2 block">
                      Nombre de participants <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.participantCount}
                      onChange={(e) => setFormData(prev => ({ ...prev, participantCount: e.target.value }))}
                      placeholder="Ex: 10"
                      className={`rounded-none ${errors.participantCount ? 'border-red-500' : ''}`}
                      data-testid="input-participants"
                    />
                    {errors.participantCount && <p className="text-red-500 text-xs mt-1">{errors.participantCount}</p>}
                  </div>
                </div>

                <div className="mb-4">
                  <Label className="text-sm text-[var(--charcoal)]/70 mb-2 block">
                    Date souhaitée (optionnel)
                  </Label>
                  <Input
                    type="date"
                    value={formData.preferredDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, preferredDate: e.target.value }))}
                    className="rounded-none"
                    data-testid="input-date"
                  />
                </div>

                <div className="mb-6">
                  <Label className="text-sm text-[var(--charcoal)]/70 mb-2 block">
                    Votre message <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Décrivez votre projet, vos besoins, les prestations souhaitées..."
                    className={`min-h-[150px] rounded-none ${errors.message ? 'border-red-500' : ''}`}
                    data-testid="textarea-message"
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-6 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300 rounded-none disabled:opacity-50"
                  data-testid="btn-submit-devis"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                </Button>
              </form>
            </SlideIn>
          </div>
        </div>
      </section>

      {/* B2B Documents Download Section */}
      {documents.length > 0 && (
        <section className="py-16 px-6 bg-[var(--champagne)]/20" data-testid="b2b-documents-section">
          <div className="max-w-[720px] mx-auto text-center">
            <FadeIn>
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-3">
                Ressources
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl font-light mb-4 text-[var(--charcoal)]">
                Documentation Atelier Événementiel
              </h2>
              <p className="text-[var(--charcoal)]/70 mb-8 max-w-lg mx-auto">
                Téléchargez nos documents pour découvrir nos offres, tarifs et conditions détaillées.
              </p>
            </FadeIn>

            {isLoadingDocs ? (
              <div className="flex justify-center py-8">
                <Loader2 size={28} className="animate-spin text-[var(--gold)]" />
              </div>
            ) : (
              <div className="flex flex-wrap justify-center gap-4">
                {documents.map((doc, index) => (
                  <FadeIn key={doc.id} delay={index * 0.1}>
                    <a
                      href={toAbsoluteUrl(doc.file_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="inline-flex items-center gap-2 border border-[var(--charcoal)] px-6 py-3 rounded-full text-sm font-medium uppercase tracking-wider text-[var(--charcoal)] hover:bg-[var(--charcoal)] hover:text-white transition-all duration-300"
                      data-testid={`download-btn-${doc.id}`}
                    >
                      <Download size={16} />
                      {doc.title}
                    </a>
                  </FadeIn>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </PageTransition>
  );
}
